import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

// FLOW[46030]: реестр секций единого центра настроек — единственный источник
// для левого меню, поиска, роутинга /v2/settings/:sectionId и бейджей влияния.

export type SettingsRole =
  | 'admin'
  | 'executive'
  | 'sales_manager'
  | 'production'
  | 'accountant'
  | 'partner';

// FLOW[47032]: центр настроек открыт всем внутренним ролям ради личных секций.
// Каждая секция всё равно фильтруется своим roles allow-list ниже.
export const SETTINGS_ROUTE_ROLES: SettingsRole[] = [
  'admin',
  'executive',
  'sales_manager',
  'production',
  'accountant',
];

export type SettingsGroupId =
  | 'org'
  | 'finance'
  | 'sales'
  | 'production'
  | 'catalog'
  | 'integrations'
  | 'notifications'
  | 'access'
  | 'documents'
  | 'system';

export interface SettingsGroupDef {
  id: SettingsGroupId;
  title: string;
}

export const SETTINGS_GROUPS: SettingsGroupDef[] = [
  { id: 'org', title: 'Организация' },
  { id: 'finance', title: 'Финансы' },
  { id: 'sales', title: 'Продажи и CRM' },
  { id: 'production', title: 'Производство' },
  { id: 'catalog', title: 'Каталог и цены' },
  { id: 'integrations', title: 'Интеграции' },
  { id: 'notifications', title: 'Уведомления' },
  { id: 'access', title: 'Доступ' },
  { id: 'documents', title: 'Документы' },
  { id: 'system', title: 'Система' },
];

/**
 * Статус влияния секции на систему:
 * - active — значения реально читаются бэкендом;
 * - not_wired — сохраняется, но бизнес-логика значение пока не читает
 *   (бейдж «не подключено» с пояснением в statusNote).
 */
export type SettingsSectionStatus = 'active' | 'not_wired';

export interface SettingsSectionDef {
  id: string;
  title: string;
  group: SettingsGroupId;
  /** Слова для поиска (включая синонимы и англ. термины). */
  keywords: string[];
  /** Короткое пояснение, что настраивает секция (показывается под заголовком). */
  description?: string;
  /**
   * FLOW[48521]: строка «на что влияет» — одно честное предложение о реальном
   * следствии настройки (что изменится в системе после сохранения). Пишется по
   * коду секции и её потребителей, без обещаний, которых система не выполняет.
   * Показывается под описанием (V2SettingsSectionHost).
   */
  impact?: string;
  /** Роли, которым видна секция. По умолчанию — только admin. */
  roles?: SettingsRole[];
  status?: SettingsSectionStatus;
  /** Пояснение для бейджа not_wired. */
  statusNote?: string;
  component: LazyExoticComponent<ComponentType>;
}

const ADMIN_ONLY: SettingsRole[] = ['admin'];

export const SETTINGS_SECTIONS: SettingsSectionDef[] = [
  // ── Организация ──────────────────────────────────────────────────────────
  {
    id: 'legal-entities',
    title: 'Юрлица и реквизиты',
    group: 'org',
    keywords: ['юрлица', 'юридические лица', 'организации', 'инн', 'кпп', 'реквизиты', 'налоговый режим', 'усн', 'печать', 'подпись', 'legal entities'],
    description: 'Юрлица компании: реквизиты, системы налогообложения, НДС, печати и подписи.',
    impact:
      'Влияет: выбранное в заказе юрлицо определяет реквизиты счёта, ставку НДС и режим налогообложения, по которым считается управленческая прибыль и комиссия менеджера.',
    roles: ['admin', 'executive'],
    component: lazy(() =>
      import('./sections/LegalEntitiesSection').then((m) => ({
        default: m.LegalEntitiesSection,
      }))
    ),
  },
  // ── Финансы ──────────────────────────────────────────────────────────────
  {
    id: 'vat',
    title: 'Ставки НДС',
    group: 'finance',
    keywords: ['ндс', 'налог', 'налоги', 'ставка', 'ставки', 'vat', 'тест ндс', 'осно', '22 процента'],
    description: 'Канонические ставки Tax Model v2: 22% для ОСНО, льготные и специальные ставки, тест расчёта.',
    impact:
      'Влияет: ставки подставляются в строки счёта и в расчёт НДС по Tax Model v2 — из них считается сумма налога в документах и в отчётности.',
    component: lazy(() =>
      import('./sections/VatSection').then((m) => ({ default: m.VatSection }))
    ),
  },
  {
    id: 'salary',
    title: 'Зарплаты и мотивация',
    group: 'finance',
    keywords: ['зарплата', 'зарплаты', 'мотивация', 'бонусы', 'оклад', 'премии', 'штрафы', 'процент от прибыли', 'salary', 'закрытие счетов', 'старые клиенты', 'комиссия', 'усн', 'налог на прибыль'],
    description: 'Расчёт зарплат менеджеров: оклад, процент от прибыли, бонусы и штрафы. Новая формула комиссии по закрытым счетам (приход − налоги − расходы = прибыль × ставка), ставка для старых клиентов и вычет УСН.',
    component: lazy(() =>
      import('./sections/SalarySection').then((m) => ({ default: m.SalarySection }))
    ),
  },
  {
    id: 'overhead',
    title: 'Накладные расходы',
    group: 'finance',
    keywords: [
      'накладные',
      'постоянные расходы',
      'суммы',
      'план',
      'факт',
      'прибыль',
      'overhead',
      'себестоимость',
      'аренда',
      'коммунальные',
      'реклама',
    ],
    description:
      'План/факт и суммы постоянных расходов по месяцу и юрлицу для управленческой прибыли.',
    roles: ['admin', 'executive'],
    status: 'active',
    component: lazy(() =>
      import('./sections/OverheadSection').then((m) => ({ default: m.OverheadSection }))
    ),
  },
  // ── Продажи и CRM ────────────────────────────────────────────────────────
  // FLOW[46588]: движок распределения обращений (волна A2)
  {
    id: 'lead-routing',
    title: 'Распределение обращений',
    group: 'sales',
    keywords: ['распределение', 'очередь', 'очереди', 'round robin', 'по кругу', 'нагрузка', 'пул', 'смены', 'график', 'дежурство', 'sla', 'маршрутизация', 'лиды', 'каналы', 'звонки', 'закрепление'],
    description: 'Очереди распределения новых обращений: алгоритмы, каналы, смены, журнал владения.',
    impact:
      'Влияет: очередь и привязка канала решают, кому именно упадёт новое обращение; без активной привязки канал работает как раньше.',
    component: lazy(() =>
      import('./sections/LeadRoutingSection').then((m) => ({
        default: m.LeadRoutingSection,
      }))
    ),
  },
  {
    id: 'pipelines',
    title: 'Воронки',
    group: 'sales',
    keywords: ['воронка', 'воронки', 'этапы', 'стадии', 'pipeline', 'crm', 'сделки', 'лиды', 'вероятность'],
    description: 'Этапы воронок CRM: порядок стадий, стартовый и финальный этапы, вероятность.',
    impact:
      'Влияет: стадии определяют колонки воронки CRM и вероятность сделки в прогнозе продаж.',
    component: lazy(() =>
      import('./sections/PipelinesSection').then((m) => ({ default: m.PipelinesSection }))
    ),
  },
  {
    id: 'order-sla',
    title: 'SLA заказов',
    group: 'sales',
    keywords: ['sla', 'сроки', 'дедлайн', 'риск', 'согласование', 'макет', 'заказы', 'таймаут'],
    description:
      'Сроки по умолчанию для новых заказов: порог риска и таймаут согласования макета. Порог риска влияет на подсветку «Заказов в риске» в дашборде продаж.',
    component: lazy(() =>
      import('./sections/OrderSlaSection').then((m) => ({ default: m.OrderSlaSection }))
    ),
  },
  // FLOW[46234]: секция «Лимиты и одобрение заказов».
  {
    id: 'order-policy',
    title: 'Лимиты и одобрение заказов',
    group: 'sales',
    keywords: [
      'кредитный лимит',
      'лимит',
      'одобрение',
      'авто-одобрение',
      'автоодобрение',
      'smart',
      'смарт',
      'заказы',
      'политика',
      'согласование',
      'credit',
      'approval',
      'порог',
    ],
    description:
      'Контроль кредитного лимита при создании заказа и правила авто-/smart-одобрения по сумме.',
    component: lazy(() =>
      import('./sections/OrderPolicySection').then((m) => ({ default: m.OrderPolicySection }))
    ),
  },
  // FLOW[46236]: секция «Справочники CRM».
  {
    id: 'crm-dictionaries',
    title: 'Справочники CRM',
    group: 'sales',
    keywords: [
      'справочник',
      'справочники',
      'сегменты',
      'сегмент клиентов',
      'источники лидов',
      'источник лида',
      'каналы лидов',
      'crm',
      'dictionaries',
      'segments',
      'lead sources',
    ],
    description: 'Сегменты клиентов и источники лидов для CRM: код, название, активность.',
    impact:
      'Влияет: сегменты и источники — это варианты выбора в карточке клиента и лида, по ним же группируются отчёты CRM.',
    component: lazy(() =>
      import('./sections/CrmDictionariesSection').then((m) => ({
        default: m.CrmDictionariesSection,
      }))
    ),
  },
  // ── Производство ─────────────────────────────────────────────────────────
  {
    id: 'material-routing',
    title: 'Материалы → принтер',
    group: 'production',
    keywords: ['материалы', 'принтер', 'маршрутизация', 'печать', 'профиль печати', 'подбор станка', 'material routing', 'printer'],
    description: 'Правила подбора принтера и профиля печати по умолчанию для материалов.',
    impact:
      'Влияет: правило подставляет принтер и профиль печати по умолчанию, когда в заказ добавляют материал.',
    component: lazy(() =>
      import('./sections/MaterialRoutingSection').then((m) => ({
        default: m.MaterialRoutingSection,
      }))
    ),
  },
  {
    id: 'print-profiles',
    title: 'Профили печати',
    group: 'production',
    keywords: ['профили печати', 'печать', 'dpi', 'проходы', 'чернила', 'скорость', 'себестоимость', 'print profiles'],
    description: 'Профили качества печати: DPI, проходы, расход и стоимость чернил, скорость.',
    impact:
      'Влияет: профиль задаёт расход и стоимость чернил и время печати — из них собирается себестоимость печати в расчёте заказа.',
    component: lazy(() =>
      import('./sections/PrintProfilesSection').then((m) => ({
        default: m.PrintProfilesSection,
      }))
    ),
  },
  {
    id: 'printers',
    title: 'Печатные станки',
    group: 'production',
    keywords: ['печатные станки', 'оборудование', 'принтеры', 'ширина', 'чернила', 'printers', 'equipment', 'roland', 'arkjet', 'sprinter'],
    description: 'Печатное оборудование и его профили печати; ширина и тип чернил.',
    impact:
      'Влияет: станок и его профили — это варианты выбора при маршрутизации материала и планировании печати.',
    component: lazy(() =>
      import('./sections/PrintersSection').then((m) => ({ default: m.PrintersSection }))
    ),
  },
  {
    id: 'postprint',
    title: 'Постпечать',
    group: 'production',
    keywords: ['постпечать', 'ламинаторы', 'ламинация', 'плоттер', 'резка', 'финиш', 'postprint', 'laminator', 'plotter'],
    description: 'Постпечатные профили: ламинаторы и плоттеры (резка) — сроки и себестоимость.',
    impact:
      'Влияет: профили ламинации и резки добавляют свою стоимость и время в расчёт заказа.',
    component: lazy(() =>
      import('./sections/PostprintOperatorProfilesSection').then((m) => ({ default: m.PostprintSection }))
    ),
  },
  {
    id: 'assembly',
    title: 'Сборка конструкций',
    group: 'production',
    keywords: ['сборка', 'пересборка', 'партнёрские конструкции', 'стенды', 'ролл-ап', 'поп-ап', 'пресс-волл', 'правила сборки', 'assembly'],
    description: 'Правила и цены сборки и пересборки конструкций (стендов) по типу и размеру.',
    impact:
      'Влияет: правило подставляет цену сборки в расчёт заказа, а цену пересборки — в счёт партнёра (полотно по размеру + пересборка).',
    component: lazy(() =>
      import('./sections/AssemblySection').then((m) => ({ default: m.AssemblySection }))
    ),
  },
  // FLOW[49953]: секция «Плоттерная резка» — тариф от сложности.
  {
    id: 'plotter-cut-pricing',
    title: 'Плоттерная резка',
    group: 'production',
    keywords: [
      'плоттерная резка',
      'контурная резка',
      'резка по контуру',
      'выборка',
      'weeding',
      'монтажная плёнка',
      'монтажка',
      'сложность реза',
      'наклейки',
      'оракал',
      'plotter cut',
      'contour cut',
    ],
    description:
      'Тариф контурной резки: приладка, ставка ₽/м², надбавка за выборку по сложности, монтажная плёнка, минимум на строку.',
    impact:
      'Влияет: цена резки в счёте партнёра и в расчёте при импорте заявки. Выключенный тариф возвращает строки с резкой в состояние «цена не определена».',
    component: lazy(() =>
      import('./sections/PlotterCutPricingSection').then((m) => ({
        default: m.PlotterCutPricingSection,
      }))
    ),
  },
  // FLOW[46235]: секция «Склад: сроки годности».
  {
    id: 'stock-expiring',
    title: 'Склад: сроки годности',
    group: 'production',
    keywords: [
      'склад',
      'сроки годности',
      'срок годности',
      'истечение',
      'просрочка',
      'партии',
      'окно предупреждения',
      'expiring',
      'stock',
      'shelf life',
    ],
    description: 'Окно предупреждения об истечении срока годности материалов на складе (в днях).',
    component: lazy(() =>
      import('./sections/StockExpiringSection').then((m) => ({
        default: m.StockExpiringSection,
      }))
    ),
  },
  // FLOW[47239]: секция «Склад: неликвид обрезков» (порог + алерт-скан).
  {
    id: 'offcut-stale',
    title: 'Склад: неликвид обрезков',
    group: 'production',
    keywords: [
      'склад',
      'обрезки',
      'обрезок',
      'неликвид',
      'залежавшиеся',
      'порог',
      'списание',
      'лом',
      'offcut',
      'stale',
      'dead stock',
    ],
    description: 'Порог неликвида обрезков (дней): подсветка в карточке «Обрезки» и складской алерт с уведомлением.',
    roles: ['admin', 'executive'],
    component: lazy(() =>
      import('./sections/OffcutStaleSection').then((m) => ({
        default: m.OffcutStaleSection,
      }))
    ),
  },
  // ── Каталог и цены ───────────────────────────────────────────────────────
  {
    id: 'pricing-rules',
    title: 'Ценовые правила',
    group: 'catalog',
    keywords: [
      'наценка',
      'надбавка',
      'процент',
      'цена',
      'цены',
      'правило',
      'правила',
      'поп-ап',
      'попап',
      'pop-up',
      'фотопанели',
      'полотна',
      'без конструкции',
      'без каркаса',
      'markup',
      'pricing',
    ],
    description:
      'Наценка на фотопанели поп-апа, заказанные без конструкции (сменные полотна к каркасу клиента).',
    impact:
      'Влияет: продажная цена поп-ап позиции в режиме «Только фотопанели (без конструкции)» считается с этой наценкой (себестоимость не меняется); других семейств, включая текстильные SegWall, правило не касается.',
    roles: ['admin', 'executive'],
    component: lazy(() =>
      import('./sections/PricingRulesSection').then((m) => ({
        default: m.PricingRulesSection,
      }))
    ),
  },
  {
    id: 'manager-tariffs',
    title: 'Внутренние тарифы',
    group: 'catalog',
    keywords: ['тариф', 'тарифы', 'себестоимость', 'внутренняя', 'константы', 'менеджер', 'полотно', 'печать', 'ролл-ап', 'маржа'],
    description:
      'Константы владельца на печатную часть позиций (полотно со сборкой) — менеджерская себестоимость в форме заказа.',
    impact:
      'Влияет: тариф заменяет расчётную материальную себестоимость в экономике строки заказа; продажные цены остаются в прайс-листах.',
    roles: ['admin'],
    component: lazy(() =>
      import('./sections/ManagerTariffsSection').then((m) => ({
        default: m.ManagerTariffsSection,
      }))
    ),
  },
  {
    id: 'google-sheets',
    title: 'Google Sheets',
    group: 'catalog',
    keywords: ['google', 'sheets', 'гугл', 'таблица', 'таблицы', 'вебхук', 'webhook', 'заявки', 'агентство', 'источник заказов', 'партнёр'],
    description:
      'Приём заявок из Google-таблицы: источник, вебхук, клиент/менеджер, прайс-лист по умолчанию и партнёр-владелец таблицы.',
    // FLOW[49222]: текст приведён к коду. Прежняя формулировка обещала лид в CRM —
    // вебхук (order-service sheets_router) лидов не создаёт вообще, он создаёт
    // черновик заказа. Обещание несуществующей функции в настройках дороже
    // неточности: по нему ищут причину «почему лид не появился».
    impact:
      'Влияет: изменение строки в подключённой таблице попадает в очередь заявок и создаёт черновик заказа, если включено «Создавать черновик заказа». Лиды в CRM этот канал не создаёт. Если выбрана партнёрская организация, заказ создаётся с её владельцем.',
    component: lazy(() =>
      import('./sections/GoogleSheetsSection').then((m) => ({
        default: m.GoogleSheetsSection,
      }))
    ),
  },
  // ── Интеграции ───────────────────────────────────────────────────────────
  {
    id: 'onec-fresh',
    title: '1C:Fresh',
    group: 'integrations',
    keywords: ['1с', '1c', 'fresh', 'фреш', 'облако', 'синхронизация', 'pull', 'загрузка', 'выгрузка', 'oauth', 'счета', 'платежи', 'клиенты', 'номенклатура', 'маппинг', 'менеджеры', 'юрлица'],
    description: 'Облачная 1C:Fresh: загрузка данных, сопоставление товаров/менеджеров/юрлиц, очередь проверки, очистка и восстановление импортов.',
    component: lazy(() =>
      import('@/components/settings/integration1c/Integration1CFreshSection').then((m) => ({
        default: m.Integration1CFreshSection,
      }))
    ),
  },
  {
    id: 'onec-local',
    title: '1С:Предприятие (OData)',
    group: 'integrations',
    keywords: ['1с', '1c', 'предприятие', 'odata', 'локальная', 'бета', 'подключение', 'сервер', 'база', 'склады', 'типы документов'],
    description: 'Прямое подключение к локальной 1С:Предприятие по OData (бета): сервер/база, тест подключения, маппинг складов и типов документов.',
    component: lazy(() =>
      import('@/components/settings/integration1c/Integration1CLocalSection').then((m) => ({
        default: m.Integration1CLocalSection,
      }))
    ),
  },
  {
    id: 'onec-sync',
    title: 'Мониторинг синхронизации 1С',
    group: 'integrations',
    keywords: ['1с', '1c', 'синхронизация', 'мониторинг', 'статус', 'дашборд', 'движение материалов', 'производство', 'обмен', 'sync'],
    description: 'Дашборд состояния синхронизации с 1С: материалы, клиенты, движение материалов и производство.',
    impact:
      'Влияет: раздел только показывает состояние обмена с 1С — очередь, историю и вебхуки; параметры подключения живут в разделах «1C:Fresh» и «1С:Предприятие (OData)».',
    component: lazy(() =>
      import('@/components/settings/integration1c/Integration1CSyncSection').then((m) => ({
        default: m.Integration1CSyncSection,
      }))
    ),
  },
  // FLOW[47384]: флаг авто-подтверждения оплаты в 1С (хвост W-B1). Единый KV
  // bank_1c_auto_confirm читается бэкендом financial-service без пересборки.
  {
    id: 'bank-1c-confirm',
    title: 'Оплаты: авто-подтверждение в 1С',
    group: 'integrations',
    keywords: [
      '1с',
      '1c',
      'оплата',
      'оплаты',
      'платёж',
      'подтверждение',
      'авторежим',
      'автоматически',
      'банк',
      'выписка',
      'бухгалтер',
      'auto confirm',
      'payment',
    ],
    description:
      'Режим подтверждения оплаты в 1С: автоматически по сопоставлению из банковской выписки или ручной задачей бухгалтеру (по умолчанию — вручную).',
    roles: ['admin', 'executive'],
    status: 'active',
    component: lazy(() =>
      import('./sections/Bank1CConfirmSection').then((m) => ({
        default: m.Bank1CConfirmSection,
      }))
    ),
  },
  {
    id: 'telephony',
    title: 'Телефония (ВАТС)',
    group: 'integrations',
    keywords: ['телефония', 'ватс', 'vats', 'матрикс', 'звонки', 'атс', 'операторы', 'закрепление', 'caller id', 'запись звонков'],
    description: 'Подключение ВАТС и закрепление операторов за менеджерами.',
    impact:
      'Влияет: параметры ВАТС используются для исходящих звонков и записи разговоров, а закрепление оператора определяет, чьим станет входящий звонок.',
    component: lazy(() =>
      import('./sections/VatsPhonySection').then((m) => ({
        default: m.VatsPhonySection,
      }))
    ),
  },
  // FLOW[46589]: секция «Заявки с сайта» (публичный intake, волна A2).
  {
    id: 'site-intake',
    title: 'Заявки с сайта',
    group: 'integrations',
    keywords: ['сайт', 'заявки', 'intake', 'формы', 'modx', 'reklamtehno', 'api ключ', 'лиды с сайта', 'utm'],
    description: 'Прямой приём заявок с сайта: тумблер, лимит, типы форм, API-ключ для MODX.',
    component: lazy(() =>
      import('./sections/SiteIntakeSection').then((m) => ({
        default: m.SiteIntakeSection,
      }))
    ),
  },
  // FLOW[48151]: секция «Тенанты и бренды» (Ш8 «Тенант-контуры»).
  {
    id: 'tenants-brands',
    title: 'Тенанты и бренды',
    group: 'integrations',
    keywords: [
      'тенант',
      'тенанты',
      'бренд',
      'бренды',
      'контур',
      'мультиарендность',
      'multi-tenant',
      'tenant',
      'сайты',
      'реестр сайтов',
      'бренд-профиль',
      'smtp',
      'почта бренда',
      'портал',
      'partner',
      'агентство',
      'reklamtehno',
    ],
    description:
      'Контуры бренда и владения: тенанты, реестр сайтов-источников (свой API-ключ на сайт), пер-тенантный бренд и SMTP событийных писем и CRM-почты (КП/счета).',
    roles: ['admin', 'executive'],
    status: 'active',
    component: lazy(() =>
      import('./sections/TenantsBrandsSection').then((m) => ({
        default: m.TenantsBrandsSection,
      }))
    ),
  },
  // FLOW[46237]: секция «Почта: приём лидов».
  {
    id: 'email-intake',
    title: 'Почта: приём заказов и лидов',
    group: 'integrations',
    keywords: [
      'почта',
      'email',
      'имап',
      'imap',
      'приём лидов',
      'ящик',
      'входящие',
      'лиды из почты',
      'заказы из почты',
      'vagiz',
      'inbound',
      'mailbox',
    ],
    description:
      'Входящие IMAP-ящики сотрудников: лиды CRM и проверяемые черновики заказов.',
    component: lazy(() =>
      import('./sections/EmailIntakeSection').then((m) => ({ default: m.EmailIntakeSection }))
    ),
  },
  // ── Уведомления ──────────────────────────────────────────────────────────
  // FLOW[46238]: секция «Каналы уведомлений».
  {
    id: 'notification-channels',
    title: 'Каналы уведомлений',
    group: 'notifications',
    keywords: [
      'уведомления',
      'каналы',
      'email',
      'почта',
      'telegram',
      'телеграм',
      'sms',
      'смс',
      'push',
      'пуш',
      'дайджест',
      'digest',
      'smtp',
      'бот',
      'доставка',
      'channels',
      'notifications',
    ],
    description:
      'Каналы доставки уведомлений: email/SMTP, Telegram, SMS, push и дайджест. Источник значения (БД/ENV), секреты и тест отправки.',
    component: lazy(() =>
      import('./sections/NotificationChannelsSection').then((m) => ({
        default: m.NotificationChannelsSection,
      }))
    ),
  },
  // FLOW[47033]: личная code-only привязка рабочего Telegram-бота.
  {
    id: 'telegram',
    title: 'Telegram-бот',
    group: 'notifications',
    keywords: [
      'telegram',
      'телеграм',
      'бот',
      'ассистент',
      'привязка',
      'подключение',
      'одноразовый код',
      'link',
      'уведомления',
    ],
    description:
      'Личная привязка рабочего бота к профилю ERP по одноразовому коду.',
    roles: SETTINGS_ROUTE_ROLES,
    status: 'active',
    component: lazy(() =>
      import('./sections/TelegramLinkSection').then((m) => ({
        default: m.TelegramLinkSection,
      }))
    ),
  },
  // ── Доступ ───────────────────────────────────────────────────────────────
  {
    id: 'users',
    title: 'Пользователи',
    group: 'access',
    keywords: ['пользователи', 'users', 'сотрудники', 'учётные записи', 'аккаунты', 'создать пользователя'],
    description: 'Пользователи: создание, роли, статус, доступ.',
    impact:
      'Влияет: роль открывает сотруднику соответствующие разделы и действия, а снятие доступа немедленно закрывает вход в систему.',
    component: lazy(() => import('./sections/UsersSection')),
  },
  {
    id: 'roles',
    title: 'Роли',
    group: 'access',
    keywords: ['роли', 'roles', 'права', 'доступ', 'разрешения', 'rbac'],
    description: 'Роли и наборы прав доступа.',
    impact:
      'Влияет: набор прав роли применяется ко всем сотрудникам с этой ролью; системные роли изменять и удалять нельзя — на них завязаны проверки в сервисах.',
    component: lazy(() =>
      import('./sections/RolesSection').then((m) => ({ default: m.RolesSection }))
    ),
  },
  {
    id: 'sessions',
    title: 'Сессии',
    group: 'access',
    keywords: ['сессии', 'sessions', 'активные входы', 'устройства', 'выход'],
    description: 'Активные сессии пользователей и их завершение.',
    impact:
      'Влияет: завершение сессии немедленно разлогинивает устройство; на саму учётную запись и её права это не влияет.',
    component: lazy(() =>
      import('./sections/SessionsSection').then((m) => ({ default: m.SessionsSection }))
    ),
  },
  {
    id: 'api-keys',
    title: 'Ключи API',
    group: 'access',
    keywords: ['ключи', 'api', 'токены', 'api keys', 'интеграционные ключи'],
    description: 'Выпуск и отзыв ключей доступа к API.',
    impact:
      'Влияет: ключ открывает доступ к API в обход входа по паролю в пределах выданных прав; отозванный ключ перестаёт работать сразу.',
    component: lazy(() =>
      import('./sections/ApiKeysSection').then((m) => ({ default: m.ApiKeysSection }))
    ),
  },
  {
    id: 'partners',
    title: 'Партнёры',
    group: 'access',
    // FLOW[49218]: секция теперь отвечает и за партнёрский склад — слова, которыми
    // владелец будет искать («списание», «триггер», «остатки»), должны находить её.
    keywords: [
      'партнёры',
      'partners',
      'партнёрский портал',
      'подрядчики',
      'склад партнёра',
      'остатки партнёра',
      'списание',
      'момент списания',
      'триггер',
      'долг партнёра',
      'каркасы',
      'рамы',
    ],
    description:
      'Партнёрские учётные записи, доступ к порталу и правила партнёрского склада: кто правит остатки и в какой момент каркас списывается.',
    impact:
      'Влияет: статус партнёра управляет входом в партнёрский портал, юрлицо по умолчанию подставляется в его документы, а момент списания задаёт, когда каркас перестаёт числиться за партнёром.',
    component: lazy(() =>
      import('./sections/PartnersSection').then((m) => ({ default: m.PartnersSection }))
    ),
  },
  // ── Документы ────────────────────────────────────────────────────────────
  {
    id: 'documents',
    title: 'Шаблоны документов',
    group: 'documents',
    keywords: ['шаблоны', 'документы', 'templates', 'упд', 'счета', 'акты', 'печатные формы'],
    description: 'Шаблоны печатных документов: просмотр, загрузка, удаление.',
    impact:
      'Влияет: раздел показывает загруженные печатные формы и позволяет их проверить, скачать или удалить; сами шаблоны заводит разработка.',
    component: lazy(() =>
      import('./sections/DocumentTemplatesSection').then((m) => ({
        default: m.DocumentTemplatesSection,
      }))
    ),
  },
  // ── Система ──────────────────────────────────────────────────────────────
  {
    id: 'saga',
    title: 'Процессы (SAGA)',
    group: 'system',
    keywords: ['процессы', 'saga', 'сага', 'оркестрация', 'очередь ошибок', 'dlq', 'мониторинг'],
    description: 'Мониторинг распределённых процессов: метрики, здоровье, очередь ошибок.',
    impact:
      'Влияет: раздел только читает состояние распределённых процессов — здесь можно перезапустить зависшие и очистить очередь ошибок, настройки не сохраняются.',
    component: lazy(() =>
      import('./sections/SagaSection').then((m) => ({ default: m.SagaSection }))
    ),
  },
  {
    id: 'policies',
    title: 'Политики себестоимости',
    group: 'system',
    keywords: ['политики', 'себестоимость', 'маржа', 'наценка', 'policies', 'cost policy'],
    description:
      'Правила расчёта себестоимости и контроля маржи с историей изменений. Пороги маржи читаются дашбордом финансов.',
    impact:
      'Влияет: база распределения и пороги маржи применяются при расчёте себестоимости и подсветке низкой маржи в заказах и отчётах.',
    component: lazy(() =>
      import('./sections/PoliciesSection').then((m) => ({ default: m.PoliciesSection }))
    ),
  },
  {
    id: 'firecrawl',
    title: 'Анализ сайтов',
    group: 'system',
    keywords: ['firecrawl', 'анализ сайтов', 'веб-интеллект', 'парсинг', 'скрейпинг'],
    description: 'Тестовая площадка веб-скрейпинга (Firecrawl).',
    impact:
      'Влияет: раздел ничего не настраивает в системе — это площадка для проверки скрейпинга сайтов вручную.',
    component: lazy(() =>
      import('./sections/FirecrawlSection').then((m) => ({ default: m.FirecrawlSection }))
    ),
  },
  {
    id: 'industry-erp',
    title: 'Отраслевая ERP',
    group: 'system',
    keywords: ['отраслевая', 'erp', 'реестр', 'industry', 'модули'],
    description: 'Реестр отраслевых ERP-модулей и их конфигурация.',
    impact:
      'Влияет: реестр показывает, какие отраслевые модули включены в сборке; изменения конфигурации применяются к их рантайму.',
    component: lazy(() =>
      import('./sections/IndustryErpSection').then((m) => ({
        default: m.IndustryErpSection,
      }))
    ),
  },
];

export function sectionRoles(section: SettingsSectionDef): SettingsRole[] {
  return section.roles && section.roles.length > 0 ? section.roles : ADMIN_ONLY;
}

export function sectionsForRole(role?: string | null): SettingsSectionDef[] {
  if (!role) return [];
  return SETTINGS_SECTIONS.filter((section) =>
    sectionRoles(section).includes(role as SettingsRole)
  );
}

export function findSection(sectionId?: string | null): SettingsSectionDef | undefined {
  if (!sectionId) return undefined;
  return SETTINGS_SECTIONS.find((section) => section.id === sectionId);
}

const groupOrder = new Map(SETTINGS_GROUPS.map((group, index) => [group.id, index]));

export function sortSectionsByGroup(sections: SettingsSectionDef[]): SettingsSectionDef[] {
  return [...sections].sort(
    (a, b) => (groupOrder.get(a.group) ?? 99) - (groupOrder.get(b.group) ?? 99)
  );
}

export function getDefaultSectionId(role?: string | null): string | undefined {
  const visible = sortSectionsByGroup(sectionsForRole(role));
  return visible[0]?.id;
}

// FLOW[46031]: поиск по настройкам — фильтр реестра по названию/ключевым словам/описанию.
export function filterSections(
  sections: SettingsSectionDef[],
  query: string
): SettingsSectionDef[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return sections;
  const terms = normalized.split(/\s+/).filter(Boolean);
  return sections.filter((section) => {
    const haystack = [section.title, section.description || '', ...section.keywords]
      .join(' ')
      .toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}
